import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, Building2, GraduationCap } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { facility_name: "", facility_type: "adult", contact_name: "", contact_role: "", email: "", phone: "", state: "", message: "" };

const inputCls =
  "w-full bg-[#0D0E11] border border-white/10 focus:border-amber-500/60 rounded-lg px-4 py-3 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] outline-none transition-colors";

export default function FacilityInquiryForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Inquiry sent. Angel is notified instantly by email and will follow up personally.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="inquiry" className="relative py-28 lg:py-36 grain" data-testid="inquiry-section">
      <div className="absolute top-0 left-1/3 w-[420px] h-[420px] rounded-full bg-amber-500/5 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-mono-tactical text-xs uppercase tracking-[0.25em] text-amber-500"
          >
            Facility Outreach
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#F3F4F6]"
            data-testid="inquiry-heading"
          >
            Bring S2S inside your walls.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 text-base sm:text-lg text-[#9CA3AF] leading-relaxed"
          >
            Tell us about your facility and population. You'll get a program outline, pilot options, and pricing — straight from Angel, not a sales team.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-10 space-y-4 font-mono-tactical text-xs tracking-[0.15em] text-[#6B7280]"
          >
            <div className="flex items-center gap-3"><span className="text-amber-500">01</span> SUBMIT THE FORM — 2 MINUTES</div>
            <div className="flex items-center gap-3"><span className="text-amber-500">02</span> ANGEL RESPONDS WITHIN 48 HOURS</div>
            <div className="flex items-center gap-3"><span className="text-amber-500">03</span> PILOT PLANNED FOR YOUR POPULATION</div>
          </motion.div>
          <motion.a
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            href="/api/syllabus.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-block text-sm text-amber-500 underline underline-offset-4 hover:text-amber-400 transition-colors"
            data-testid="inquiry-download-syllabus-btn"
          >
            Prefer to share it on paper? ↓ Download the program one-pager (PDF)
          </motion.a>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-7 bg-[#151821] border border-white/10 rounded-2xl p-8 lg:p-10"
          data-testid="inquiry-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">FACILITY NAME *</label>
              <input required value={form.facility_name} onChange={set("facility_name")} placeholder="e.g. Ironwood State Correctional" className={inputCls} data-testid="inquiry-facility-name" />
            </div>

            <div className="sm:col-span-2">
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">FACILITY TYPE *</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "adult", label: "Adult Facility", icon: Building2 },
                  { key: "juvenile", label: "Juvenile Facility", icon: GraduationCap },
                ].map((t) => (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => setForm({ ...form, facility_type: t.key })}
                    className={`flex items-center gap-2.5 px-4 py-3 rounded-lg border text-sm transition-colors ${
                      form.facility_type === t.key
                        ? "border-amber-500 bg-amber-500/10 text-amber-500 font-semibold"
                        : "border-white/10 text-[#9CA3AF] hover:border-white/25"
                    }`}
                    data-testid={`inquiry-type-${t.key}`}
                  >
                    <t.icon size={16} /> {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">YOUR NAME *</label>
              <input required value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" className={inputCls} data-testid="inquiry-contact-name" />
            </div>
            <div>
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">ROLE / TITLE</label>
              <input value={form.contact_role} onChange={set("contact_role")} placeholder="e.g. Warden, Program Director" className={inputCls} data-testid="inquiry-contact-role" />
            </div>
            <div>
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">EMAIL *</label>
              <input required type="email" value={form.email} onChange={set("email")} placeholder="you@facility.gov" className={inputCls} data-testid="inquiry-email" />
            </div>
            <div>
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">PHONE</label>
              <input value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" className={inputCls} data-testid="inquiry-phone" />
            </div>
            <div className="sm:col-span-2">
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">STATE</label>
              <input value={form.state} onChange={set("state")} placeholder="e.g. TX" maxLength={2} className={`${inputCls} uppercase`} data-testid="inquiry-state" />
            </div>
            <div className="sm:col-span-2">
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">TELL US ABOUT YOUR POPULATION & GOALS</label>
              <textarea value={form.message} onChange={set("message")} rows={4} placeholder="Population size, current programming, what you'd want S2S to change..." className={inputCls} data-testid="inquiry-message" />
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0D0E11] font-bold px-7 py-4 rounded-full transition-all"
            data-testid="inquiry-submit-btn"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {sending ? "Sending..." : "Send Facility Inquiry"}
          </button>
          <p className="mt-4 text-center font-mono-tactical text-[10px] tracking-[0.15em] text-[#6B7280]">
            GOES DIRECTLY TO ANGEL GARCIA — NO GATEKEEPERS
          </p>
        </motion.form>
      </div>
    </section>
  );
}
